<script setup lang="ts">
import type { ReportContentVisit } from '~/models/Report'
import type { GroupMember } from '~/models/Group'
import type { BaseItem } from '~/models/Content/BaseItem'
import type { ReportFilters } from '~/models/ReportFilters'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons'
import { PendoFeature } from '~/models/Pendo'
import Button from '~/components/ui/Button.vue'
import OpenActivityComp from '~/components/report/OpenActivity.vue'

const props = defineProps<{
  content: BaseItem
  visit?: ReportContentVisit
  groupMembers?: GroupMember[]
  showActivityDetail?: boolean
  activeSection?: 'main' | 'task' | 'assignment'
  sectionIndex?: number
  selectedUserId?: string
  filters?: ReportFilters
}>()
const emit = defineEmits<{
  close: []
  openDetail: [section: 'main' | 'task' | 'assignment', index?: number, userId?: string]
  closeDetail: []
}>()
const { t } = useI18n()

const displayedVisit = computed<ReportContentVisit | undefined>(() => props.visit)

function closeView() {
  emit('close')
}

function handleOpenDetail(section: 'main' | 'task' | 'assignment', index?: number, userId?: string) {
  emit('openDetail', section, index, userId)
}

function handleCloseDetail() {
  emit('closeDetail')
}
</script>

<template>
  <section class="min-h-[40vh] text-sm sm:text-base">
    <div v-if="!props.showActivityDetail" class="mb-3 sm:mb-4">
      <Button
        variant="solid"
        shade="dark"
        color="green"
        class="!bg-green-40 hover:!bg-green-40/90 w-auto"
        :data-pendo="PendoFeature.ActivityReports.BackToFrontpage"
        @click="closeView"
      >
        <FontAwesomeIcon
          :icon="faArrowLeft"
          class="h-4 w-4 mr-2"
          aria-hidden="true"
        />
        {{ t('back') }}
      </Button>
    </div>

    <div class="w-full overflow-x-auto">
      <component
        :is="OpenActivityComp"
        :content="props.content"
        :visit="displayedVisit"
        :group-members="props.groupMembers"
        :show-activity-detail="props.showActivityDetail"
        :active-section="props.activeSection"
        :section-index="props.sectionIndex"
        :selected-user-id="props.selectedUserId"
        :filters="props.filters"
        @open-detail="handleOpenDetail"
        @close-detail="handleCloseDetail"
      />
    </div>
  </section>
</template>
